import { useState, useEffect } from 'react';
import { api, formatPrice } from '@/lib/api';
import { ShoppingCart, DollarSign, Eye, AlertTriangle, Package } from 'lucide-react';

export default function AdminDashboard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get('/admin/dashboard')
            .then(r => setData(r.data))
            .catch(e => console.error(e))
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <div className="space-y-6">
                <h1 className="text-2xl font-heading font-bold text-cp-text-main">Dashboard</h1>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {[1,2,3,4].map(i => <div key={i} className="h-28 skeleton rounded-xl" />)}
                </div>
            </div>
        );
    }

    const stats = [
        { label: 'Ventas Totales', value: formatPrice(data?.total_revenue || 0), icon: DollarSign, color: 'bg-cp-success/10 text-cp-success' },
        { label: 'Pedidos Hoy', value: data?.today_orders || 0, icon: ShoppingCart, color: 'bg-cp-secondary-soft text-cp-secondary' },
        { label: 'Carritos Abandonados', value: data?.abandoned_carts || 0, icon: AlertTriangle, color: 'bg-cp-warning/10 text-cp-warning' },
        { label: 'Visitas Hoy', value: `${data?.today_visits || 0} (${data?.unique_visitors || 0} unicas)`, icon: Eye, color: 'bg-cp-primary-soft text-cp-brand' },
    ];

    const statusColors = {
        pending: 'bg-cp-warning/10 text-cp-warning',
        confirmed: 'bg-cp-secondary-soft text-cp-secondary',
        shipped: 'bg-cp-primary-soft text-cp-brand',
        delivered: 'bg-cp-success/10 text-cp-success',
        cancelled: 'bg-cp-error/10 text-cp-error',
    };
    const statusLabels = {
        pending: 'Pendiente', confirmed: 'Confirmado', shipped: 'Enviado', delivered: 'Entregado', cancelled: 'Cancelado',
    };

    return (
        <div className="space-y-6" data-testid="admin-dashboard">
            <h1 className="text-2xl font-heading font-bold text-cp-text-main">Dashboard</h1>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-stats">
                {stats.map((s, i) => (
                    <div key={i} className="bg-white border border-cp-border rounded-xl p-4 lg:p-5">
                        <div className={`inline-flex items-center justify-center w-10 h-10 rounded-lg ${s.color} mb-3`}>
                            <s.icon size={18} />
                        </div>
                        <p className="text-cp-text-muted text-xs mb-1">{s.label}</p>
                        <p className="font-heading font-bold text-cp-text-main text-lg">{s.value}</p>
                    </div>
                ))}
            </div>

            {/* Recent Orders */}
            <div className="bg-white border border-cp-border rounded-xl overflow-hidden" data-testid="recent-orders">
                <div className="p-4 lg:p-5 border-b border-cp-border-soft">
                    <h2 className="font-heading font-bold text-cp-text-main">Pedidos Recientes</h2>
                </div>
                {data?.recent_orders?.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-cp-border-soft bg-cp-bg-alt">
                                    <th className="text-left p-3 text-cp-text-muted font-medium">Pedido</th>
                                    <th className="text-left p-3 text-cp-text-muted font-medium">Cliente</th>
                                    <th className="text-left p-3 text-cp-text-muted font-medium hidden sm:table-cell">Telefono</th>
                                    <th className="text-right p-3 text-cp-text-muted font-medium">Total</th>
                                    <th className="text-center p-3 text-cp-text-muted font-medium">Estado</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.recent_orders.map(order => (
                                    <tr key={order.id} className="border-b border-cp-border-soft last:border-0 hover:bg-cp-bg-alt/50">
                                        <td className="p-3 font-mono text-xs text-cp-text-body">#{order.id?.slice(0,8)}</td>
                                        <td className="p-3 text-cp-text-main font-medium">{order.customer_name}</td>
                                        <td className="p-3 text-cp-text-body hidden sm:table-cell">{order.customer_phone}</td>
                                        <td className="p-3 text-right font-semibold text-cp-brand">{formatPrice(order.total)}</td>
                                        <td className="p-3 text-center">
                                            <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${statusColors[order.status] || 'bg-cp-surface text-cp-text-muted'}`}>
                                                {statusLabels[order.status] || order.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="p-8 text-center text-cp-text-muted">
                        <Package size={32} className="mx-auto mb-2 opacity-50" />
                        <p>No hay pedidos aun</p>
                    </div>
                )}
            </div>
        </div>
    );
}
